/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Route } from './+types/_app.$slug.$productId[.html]';
import ProductPage, { loader as productLoader, type ProductPageData } from './_app.product.$productId';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';

/**
 * Serves two-segment `.html` product URLs (`/{slug}/{productId}.html`) for SFRA/SiteGenesis URL
 * parity. Per the route-precedence rules in `docs/README-CONTENT-ASSET-ROUTE.md` (section 5), a
 * two-segment `.html` URL is exclusively a product URL — the single-segment `.html` space
 * (`_app.$contentId[.html].tsx`) is what resolves content assets.
 *
 * `slug` is decorative only: legacy PDP URLs carry a human-readable slug ahead of the SKU, but
 * this app has no slug/id resolution table, so the slug is accepted as-is (not validated) and only
 * `productId` is used to resolve the product — same "id is authoritative" assumption the
 * single-segment content route makes. Renders the existing `/product/:productId` route's own
 * loader/component *in place* (no redirect), reusing its full PDP rendering rather than
 * duplicating it. The canonical `<link>`/OG url still points at `/product/:productId` so the
 * product isn't indexed once per slug variant.
 */
export async function loader(args: Route.LoaderArgs): Promise<ProductPageData> {
    const { request, params } = args;
    const { productId } = params;

    const productData = await productLoader({
        ...args,
        params: { productId },
    } as unknown as Parameters<typeof productLoader>[0]);

    // Preserve any multi-site /:siteId/:localeId prefix already on the request path — replace
    // only the trailing /{slug}/{productId}.html with /product/{productId}.
    const url = new URL(request.url);
    const segments = url.pathname.split('/');
    const canonicalPathname = `${segments.slice(0, -2).join('/')}/product/${productId}`;
    productData.pageUrl = buildCanonicalUrl(getPublicOrigin(request), canonicalPathname, url.search);

    return productData;
}

export default function SlugProductRoute({ loaderData }: { loaderData: ProductPageData }) {
    return <ProductPage loaderData={loaderData} />;
}
